import React from 'react';
import { Colors, PrimaryButton, SizeableRoundedDialog, Text } from 'library';
import UserAttributesApi from 'shared/UserAttributesApi';
import { useUserContext } from 'UserContext';

import type { SubPoolSummary } from '../types';
import { getElcIcon } from './elcIcons';
import {
    FadeSlideIn,
    AnimatedLogo,
    HeaderSection,
    ModalBody,
    PartnershipCard,
    BenefitsList,
    BenefitItem,
    IconCircle,
} from './elcStyles';

export function ELCWelcomeModal({
    partner,
    open,
    onClose,
}: {
    partner: SubPoolSummary;
    open: boolean;
    onClose: () => void;
}) {
    const { user, refreshAlreadyLoggedInUser } = useUserContext();

    function closeModal() {
        if (user?.sub_pool_welcome_attribute) {
            UserAttributesApi.remove(user?.sub_pool_welcome_attribute.id).then(refreshAlreadyLoggedInUser);
        }
        onClose();
    }

    return (
        <SizeableRoundedDialog
            id={`${partner.slug ?? partner.id}-elc-welcome-modal`}
            closeButton={true}
            open={open}
            onClose={closeModal}
            style={{ maxWidth: 520 }}
            contentStyle={{ padding: 0, overflow: 'hidden' }}
            TransitionProps={{ timeout: 400 }}
        >
            <HeaderSection>
                <FadeSlideIn delay={150}>
                    <Text variant="display" color={Colors.white}>
                        Welcome to Tandem!
                    </Text>
                </FadeSlideIn>
                <FadeSlideIn delay={250}>
                    <Text variant="body1" color={Colors.white} textStyle={{ paddingTop: 6 }}>
                        You&apos;re part of something special.
                    </Text>
                </FadeSlideIn>
            </HeaderSection>
            <ModalBody>
                <FadeSlideIn delay={200}>
                    <PartnershipCard>
                        <AnimatedLogo
                            src={partner.logo_url ?? ''}
                            alt={partner.full_name ?? partner.name}
                            width={partner.logo_width_modal ?? undefined}
                        />
                        <Text variant="h1" bold>
                            {`${partner.name}'s Substitute Pool`}
                        </Text>
                        <Text variant="body2" color={Colors.mediumGrey} textStyle={{ paddingTop: 2 }}>
                            Powered by Tandem
                        </Text>
                        <Text variant="body1" textStyle={{ paddingTop: 12 }}>
                            {partner.description}
                        </Text>
                    </PartnershipCard>
                </FadeSlideIn>
                <BenefitsList>
                    {partner.benefits.map((benefit, index) => {
                        const Icon = getElcIcon(benefit.icon);
                        return (
                            <FadeSlideIn key={benefit.text} delay={350 + index * 100}>
                                <BenefitItem>
                                    <IconCircle size={36}>
                                        {Icon ? <Icon style={{ color: Colors.white, fontSize: 20 }} /> : null}
                                    </IconCircle>
                                    <Text variant="body1">{benefit.text}</Text>
                                </BenefitItem>
                            </FadeSlideIn>
                        );
                    })}
                </BenefitsList>
                <FadeSlideIn delay={700}>
                    <PrimaryButton
                        id={`${partner.slug ?? partner.id}-elc-welcome-dismiss`}
                        onClick={closeModal}
                        buttonStyle={{ marginTop: 10, width: '100%', borderRadius: 999, padding: '14px 0' }}
                        textStyle={{ fontSize: 16 }}
                    >
                        Get Started
                    </PrimaryButton>
                </FadeSlideIn>
            </ModalBody>
        </SizeableRoundedDialog>
    );
}
